import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  EAutocompleteOption,
  EButton,
  EFlatButton,
  EForm,
} from 'webapp-lib/Components';
import {classy} from 'webapp-lib/utils';

import {CasusAutocomplete} from '#/Autocomplete';
import {DialogContentActions} from '#/Dialog/styles';
import {FolderTreeSelect} from '#/FolderTree';
import {CasusSearchItem} from '@lexian/api/casus-api';
import {useMasterdata} from '_/hooks/useMasterdata';
import {I18NextNS} from '_/i18n';
import {WizardStepProps} from '_/sharedComponents/CreateFromTemplateWizard';
import {StyledDoubleColumnFillPlaceholders} from '_/sharedComponents/CreateFromTemplateWizard/fill-placeholders/FillPlaceholders.styles';
import {loadSubfolders} from '_/utilities/folders';
import {
  Control,
  EFAutocomplete,
  EFTextArea,
  EFTextInput,
  requiredRule,
  useForm,
  useWatch,
} from '_/utilities/form';
import {
  EXTENSION_FROM_TEMPLATE_FILE_TYPE,
  State,
  TEMPLATE_FILE_TYPE_TO_DOCUMENT_TYPE,
} from '_/utilities/types';
import {debouncedValidateAvailableFileName} from '_/utilities/validateAvailableFileNames';

import {CreateDocumentFromTemplateStore} from '../../store';

import {
  FoldertreeColumn,
  FormColumn,
  FormColumnWrapper,
} from './ChooseTemplate.styles';

type Props = {
  localStore: CreateDocumentFromTemplateStore;
  forReporting?: boolean;
} & WizardStepProps;

enum ChooseTemplateFormFields {
  CasusId = 'casusId',
  FolderId = 'folderId',
  TemplateId = 'templateId',
  LocaleId = 'localeId',
  FileName = 'fileName',
  LogDescription = 'logDescription',
}

type ChooseTemplateFormType = {
  [ChooseTemplateFormFields.CasusId]: string;
  [ChooseTemplateFormFields.FolderId]: string;
  [ChooseTemplateFormFields.TemplateId]: string;
  [ChooseTemplateFormFields.LocaleId]: string;
  [ChooseTemplateFormFields.FileName]: string;
  [ChooseTemplateFormFields.LogDescription]: string;
};

const getLocaleToBeSet = (
  localeOptions: EAutocompleteOption[],
  casusCorrespondenceLocale: string | undefined,
  watchedLocale: string
): string | undefined => {
  if (localeOptions.length > 0) {
    const localeOptionValues = localeOptions.map((option) => option.value);
    if (
      casusCorrespondenceLocale &&
      localeOptionValues.includes(casusCorrespondenceLocale)
    ) {
      return casusCorrespondenceLocale;
    }
    if (localeOptionValues.includes(watchedLocale)) {
      return watchedLocale;
    }
    return localeOptions[0].value;
  }
  return undefined;
};

const UChooseTemplate = ({
  localStore,
  backLabel,
  nextLabel,
  onNextClick,
  onBackClick,
  forReporting = false,
}: Props): React.ReactElement => {
  const {t} = useTranslation<I18NextNS>('documents');
  const {translatedLocales} = useMasterdata();

  const [casusCorrespondenceLocale, setCasusCorrespondenceLocale] = useState<
    string | undefined
  >();

  const form = useForm<ChooseTemplateFormType>({
    defaultValues: {
      [ChooseTemplateFormFields.CasusId]: localStore.chooseTemplateData.casusId,
      [ChooseTemplateFormFields.FolderId]:
        localStore.chooseTemplateData.folderId,
      [ChooseTemplateFormFields.TemplateId]:
        localStore.chooseTemplateData.templateId,
      [ChooseTemplateFormFields.LocaleId]:
        localStore.chooseTemplateData.localeId,
      [ChooseTemplateFormFields.FileName]:
        localStore.chooseTemplateData.fileName,
      [ChooseTemplateFormFields.LogDescription]:
        localStore.chooseTemplateData.logDescription,
    },
    onSubmit: (values) => {
      const templateFile = localStore.getTemplateFile(
        values.templateId,
        values.localeId
      );
      if (!templateFile) {
        throw Error('Invalid template file id');
      }
      localStore.setChooseTemplateData({
        ...values,
        templateFileId: templateFile.id,
      });
      return onNextClick();
    },
  });

  const watchedCasusId = form.watch(ChooseTemplateFormFields.CasusId);
  const watchedFolderId = form.watch(ChooseTemplateFormFields.FolderId);
  const watchedTemplateId = form.watch(ChooseTemplateFormFields.TemplateId);
  const watchedLocale = form.watch(ChooseTemplateFormFields.LocaleId);

  useTrackChooseTemplateStepModifications(localStore, form.control);

  useEffect(() => {
    if (watchedCasusId && watchedFolderId) {
      localStore.fetchFolderOverview(watchedCasusId, watchedFolderId);
    }
    // We only want to fetch a new overview when the casus or the localStore changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStore, watchedCasusId]);

  useEffect(() => {
    if (localStore.folderLoadable.data) {
      form.setValue(
        ChooseTemplateFormFields.FolderId,
        localStore.folderLoadable.data?.activeFolder.id,
        {shouldValidate: true}
      );
    }
    // Do not react to changes of form
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStore.folderLoadable.data]);

  const fileNameRules = {
    ...requiredRule,
    validate: (value: string) => {
      const templateFileType = localStore.getTemplateFile(
        watchedTemplateId,
        watchedLocale
      )?.type;
      if (!templateFileType) {
        return false;
      }
      if (templateFileType === 'ZIP' || !watchedCasusId) {
        // validation for zip templates is not supported
        return;
      }
      return debouncedValidateAvailableFileName({
        fileTitle: value,
        extension: EXTENSION_FROM_TEMPLATE_FILE_TYPE[templateFileType],
        type: TEMPLATE_FILE_TYPE_TO_DOCUMENT_TYPE[templateFileType],
        folderId: watchedFolderId,
        casusId: watchedCasusId,
      });
    },
  };

  const templateOptions = useMemo(
    () =>
      localStore.templateLoadable.data?.map((templateFileInResponse) => ({
        value: templateFileInResponse.id,
        displayText: templateFileInResponse.text,
      })) || [],
    [localStore.templateLoadable.data]
  );

  useEffect(
    () => {
      if (watchedTemplateId === '' && templateOptions.length > 0) {
        form.setValue(
          ChooseTemplateFormFields.TemplateId,
          templateOptions[0].value,
          {shouldValidate: true}
        );
      }
    },
    // Only react to changes to templateOptions
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templateOptions]
  );

  const localeOptions = useMemo(
    () =>
      localStore.getLocalesForSelectedTemplate(
        watchedTemplateId,
        translatedLocales
      ),
    [localStore, watchedTemplateId, translatedLocales]
  );

  useEffect(
    () => {
      const localeToBeSet = getLocaleToBeSet(
        localeOptions,
        casusCorrespondenceLocale,
        watchedLocale
      );
      if (localeToBeSet) {
        form.setValue(ChooseTemplateFormFields.LocaleId, localeToBeSet, {
          shouldValidate: true,
        });
      }
    },
    // Only react to changes to localeOptions and casusCorrespondenceLocale
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [localeOptions, casusCorrespondenceLocale]
  );

  const onCasusChangeHandler = useCallback((casus?: CasusSearchItem) => {
    setCasusCorrespondenceLocale(casus?.correspondenceLocale);
  }, []);

  return (
    <StyledDoubleColumnFillPlaceholders
      className={classy({'single-column': forReporting})}
    >
      <EForm onSubmit={form.handleSubmit}>
        <FormColumnWrapper>
          {!forReporting ? (
            <FormColumn>
              <CasusAutocomplete
                control={form.control}
                label={t('create.fromTemplate.dialog.casus')}
                name={ChooseTemplateFormFields.CasusId}
                rules={requiredRule}
                searchFiltersPersistenceKey='chooseTemplateForm'
                onCasusChange={onCasusChangeHandler}
                onSelectedCasusIsFilteredOut={() =>
                  form.setValue(ChooseTemplateFormFields.CasusId, '')
                }
              />
              {watchedCasusId ? (
                <FoldertreeColumn>
                  <FolderTreeSelect
                    control={form.control}
                    label={t('create.fromTemplate.dialog.directory')}
                    loadingStatus={localStore.folderLoadable.state}
                    loadSubFolders={(folderId) =>
                      loadSubfolders(watchedCasusId, folderId)
                    }
                    name={ChooseTemplateFormFields.FolderId}
                    rootFolder={localStore.folderLoadable.data?.rootFolder}
                    rules={requiredRule}
                  />
                </FoldertreeColumn>
              ) : null}
            </FormColumn>
          ) : null}
          <FormColumn>
            <EFAutocomplete
              control={form.control}
              isLoading={localStore.templateLoadable.state === State.Loading}
              label={
                forReporting
                  ? t('create.fromTemplate.dialog.report')
                  : t('create.fromTemplate.dialog.template')
              }
              name={ChooseTemplateFormFields.TemplateId}
              options={templateOptions}
              rules={requiredRule}
            />
            <EFAutocomplete
              control={form.control}
              label={t('create.fromTemplate.dialog.locale')}
              name={ChooseTemplateFormFields.LocaleId}
              options={localeOptions}
              rules={requiredRule}
            />
            {!forReporting ? (
              <>
                <EFTextInput
                  control={form.control}
                  label={t('create.fromTemplate.dialog.filename')}
                  name={ChooseTemplateFormFields.FileName}
                  rules={fileNameRules}
                  autoFocus
                />
                <EFTextArea
                  control={form.control}
                  label={t('create.fromTemplate.dialog.logDescription')}
                  minRows={3}
                  name={ChooseTemplateFormFields.LogDescription}
                />
              </>
            ) : null}
          </FormColumn>
        </FormColumnWrapper>
        <DialogContentActions stepperActions={true}>
          <EFlatButton testId='back' onClick={onBackClick}>
            {backLabel}
          </EFlatButton>
          <EButton disabled={form.disableActions} testId='next'>
            {nextLabel}
          </EButton>
        </DialogContentActions>
      </EForm>
    </StyledDoubleColumnFillPlaceholders>
  );
};

export default observer(UChooseTemplate);

const useTrackChooseTemplateStepModifications = (
  store: CreateDocumentFromTemplateStore,
  control: Control<ChooseTemplateFormType>
) => {
  const watchedFilename = useWatch({
    control,
    name: ChooseTemplateFormFields.FileName,
  });
  const watchedLogDescription = useWatch({
    control,
    name: ChooseTemplateFormFields.LogDescription,
  });

  useEffect(() => {
    if (watchedFilename !== '') {
      store.setFileNameHasBeenModified();
    }
    // Only react to changes to watchedFileName
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFilename]);

  useEffect(() => {
    if (watchedLogDescription !== '') {
      store.setLogDescriptionHasBeenModified();
    }
    // Only react to changes to watchedFileName
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedLogDescription]);
};
